import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Products master — list + permission-gated create/edit/delete.
 * All actions gate purely on the effective permission set
 * (products.add / products.edit / products.delete). Any role granted these
 * permissions from Roles & Permissions gains access on next login.
 */
export default function ProductsReadOnly() {
  const { can } = useAuth();
  const canAdd = can("products.add");
  const canEdit = can("products.edit");
  const canDelete = can("products.delete");
  const showActions = canEdit || canDelete;
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((p) => {
    if (search && !`${p.name} ${p.model || ""} ${p.category || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (brand && (p.brand || "").toLowerCase() !== brand.toLowerCase()) return false;
    return true;
  });

  const onDelete = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${product.id}`);
      toast.success(`Deleted ${product.name}`);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-5" data-testid="products-readonly-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Products</h1>
          <p className="text-sm text-slate-500 mt-1">
            {rows.length} products in master{canAdd || canEdit || canDelete ? "" : " · read-only view"}.
          </p>
        </div>
        {canAdd && (
          <Button onClick={() => setShowCreate(true)} data-testid="products-create-btn">
            <Plus size={14} className="mr-1" /> New Product
          </Button>
        )}
      </div>
      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input placeholder="Search name / model / category…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="products-search" />
        <Input placeholder="Filter by brand" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="products-filter-brand" />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b"><tr>
              <Th>Brand</Th><Th>Category</Th><Th>Product Name</Th><Th>Model</Th><Th>Active</Th>
              {showActions && <Th align="right">Actions</Th>}
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={showActions ? 6 : 5} className="py-12 text-center text-slate-400">No products.</td></tr>}
              {filtered.slice(0, 1000).map((p) => (
                <tr key={p.id} data-testid={`product-${p.id?.slice(0, 8)}`}>
                  <td className="font-medium">{p.brand || "—"}</td>
                  <td>{p.category || p.product_category || "—"}</td>
                  <td>{p.name}</td>
                  <td className="font-mono text-xs">{p.model || "—"}</td>
                  <td className="text-xs">{p.active === false ? "No" : "Yes"}</td>
                  {showActions && (
                    <td className="text-right whitespace-nowrap">
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(p)} data-testid={`products-edit-${p.id?.slice(0, 8)}`}>
                          <Edit size={14} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => onDelete(p)} data-testid={`products-delete-${p.id?.slice(0, 8)}`}>
                          <Trash2 size={14} className="text-rose-600" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 1000 && <div className="px-3 py-2 text-xs text-slate-500">Showing first 1000 of {filtered.length}. Use search/filter to narrow.</div>}
        </div>
      </CardContent></Card>

      <ProductCreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <ProductEditDialog product={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function ProductCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    brand: "", name: "", model: "", product_category: "", specifications: "",
    product_size: "", warranty_clause: "12 months",
    comprehensive_months: 12, compressor_months: 0, motor_months: 0,
    parts_months: 0, service_months: 0,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.brand?.trim() || !form.name?.trim() || !form.model?.trim()) {
      toast.error("Brand, Product Name and Model are required");
      return;
    }
    if (!form.product_size?.trim()) {
      toast.error("Product Size is required"); return;
    }
    if (!form.warranty_clause?.trim()) {
      toast.error("Warranty Clause is required"); return;
    }
    const coverage = Number(form.comprehensive_months || 0) + Number(form.compressor_months || 0) +
                     Number(form.motor_months || 0) + Number(form.parts_months || 0) +
                     Number(form.service_months || 0);
    if (coverage <= 0) {
      toast.error("Enter at least one warranty period (in months)"); return;
    }
    try {
      const { data } = await api.post("/products", form);
      toast.success(`Product created: ${data.name}`);
      onCreated && onCreated();
      onClose();
      setForm({ brand: "", name: "", model: "", product_category: "", specifications: "",
                product_size: "", warranty_clause: "12 months",
                comprehensive_months: 12, compressor_months: 0, motor_months: 0,
                parts_months: 0, service_months: 0 });
    } catch (e) {
      toast.error("Could not create product", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-create-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New Product</DialogTitle>
          <DialogDescription>
            Admin / Product Manager direct-create. Same product NAME with different MODEL is allowed;
            only MODEL number is enforced unique.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pair label="Brand *">
            <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="product-create-brand" placeholder="e.g. HISENSE" />
          </Pair>
          <Pair label="Category">
            <Input value={form.product_category} onChange={(e) => set("product_category", e.target.value)} data-testid="product-create-category" placeholder="e.g. TV" />
          </Pair>
          <Pair label="Product Name *" col2>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-create-name" />
          </Pair>
          <Pair label="Model Number *">
            <Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="product-create-model" />
          </Pair>
          <Pair label="Size *">
            <Input value={form.product_size} onChange={(e) => set("product_size", e.target.value)} data-testid="product-create-size" placeholder='e.g. "43"' />
          </Pair>
          <Pair label="Warranty Clause *" col2>
            <Input value={form.warranty_clause} onChange={(e) => set("warranty_clause", e.target.value)} data-testid="product-create-warranty-clause" placeholder="e.g. 12 months + 24 months on compressor" />
          </Pair>
          <Pair label="Comprehensive (mo)">
            <Input type="number" min="0" value={form.comprehensive_months} onChange={(e) => set("comprehensive_months", Number(e.target.value || 0))} data-testid="product-create-comp-months" />
          </Pair>
          <Pair label="Compressor (mo)">
            <Input type="number" min="0" value={form.compressor_months} onChange={(e) => set("compressor_months", Number(e.target.value || 0))} />
          </Pair>
          <Pair label="Motor (mo)">
            <Input type="number" min="0" value={form.motor_months} onChange={(e) => set("motor_months", Number(e.target.value || 0))} />
          </Pair>
          <Pair label="Parts (mo)">
            <Input type="number" min="0" value={form.parts_months} onChange={(e) => set("parts_months", Number(e.target.value || 0))} />
          </Pair>
          <Pair label="Service (mo)" col2>
            <Input type="number" min="0" value={form.service_months} onChange={(e) => set("service_months", Number(e.target.value || 0))} />
          </Pair>
          <Pair label="Specifications" col2>
            <Input value={form.specifications} onChange={(e) => set("specifications", e.target.value)} />
          </Pair>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="product-create-submit">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Pair({ label, col2, children }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function ProductEditDialog({ product, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(product ? { ...product } : null); }, [product]);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.brand?.trim() || !form.name?.trim() || !form.model?.trim()) {
      toast.error("Brand, Product Name and Model are required"); return;
    }
    try {
      const payload = {
        brand: form.brand, name: form.name, model: form.model,
        product_category: form.product_category || form.category,
        specifications: form.specifications,
      };
      await api.put(`/products/${product.id}`, payload);
      toast.success(`Updated ${form.name}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not update product", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Product</DialogTitle>
          <DialogDescription>Requires `products.edit` permission.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pair label="Brand *">
            <Input value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="product-edit-brand" />
          </Pair>
          <Pair label="Category">
            <Input value={form.product_category || form.category || ""} onChange={(e) => set("product_category", e.target.value)} data-testid="product-edit-category" />
          </Pair>
          <Pair label="Product Name *" col2>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="product-edit-name" />
          </Pair>
          <Pair label="Model Number *" col2>
            <Input value={form.model || ""} onChange={(e) => set("model", e.target.value)} data-testid="product-edit-model" />
          </Pair>
          <Pair label="Specifications" col2>
            <Input value={form.specifications || ""} onChange={(e) => set("specifications", e.target.value)} />
          </Pair>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="product-edit-submit">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
